import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import WhatsAppInteractiveOptions from '../WhatsAppInteractiveOptions';
import { startInboxRecovery } from '../../utils/whatsappInboxSync';

const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
};

export default function AgencySupervisor() {
    const { authFetch, logout } = useAuth();
    const [profile, setProfile] = useState(null);
    const [data, setData] = useState({ episodes: [], total: 0 });
    const [view, setView] = useState('operational');
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const [selected, setSelected] = useState(null);
    const [messages, setMessages] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const selectedId = useRef(null);
    const sequence = useRef(0);
    const cancelRefresh = useCallback(() => { sequence.current += 1; }, []);
    const params = new URLSearchParams({ view, search, limit: '50', offset: String(offset) }).toString();
    const read = useCallback(async (url, options) => {
        const response = await authFetch(url, options);
        if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw new Error(detail.error || 'No se pudo cargar la información.');
        }
        return response;
    }, [authFetch]);

    const refresh = useCallback(async () => {
        const current = ++sequence.current;
        try {
            const [profileResponse, inboxResponse] = await Promise.all([
                read('/api/agency/me'), read(`/api/agency/inbox?${params}`),
            ]);
            const [nextProfile, nextData] = await Promise.all([profileResponse.json(), inboxResponse.json()]);
            if (current !== sequence.current) return;
            setProfile(nextProfile);
            setData(nextData);
            setError('');
        } catch (e) {
            if (current === sequence.current) { setError(e.message); setData({ episodes: [], total: 0 }); }
        } finally { if (current === sequence.current) setLoading(false); }
    }, [read, params]);

    useEffect(() => {
        setLoading(true);
        refresh();
        const stop = startInboxRecovery(refresh, 15000);
        return () => { cancelRefresh(); stop(); };
    }, [refresh, cancelRefresh]);

    const loadMessages = useCallback(async (episode) => {
        try {
            const response = await read(`/api/agency/episodes/${episode.id}/messages`);
            const rows = await response.json();
            if (selectedId.current === episode.id) setMessages(rows);
        } catch (e) {
            if (selectedId.current === episode.id) { setMessages([]); setError(e.message); }
        }
    }, [read]);
    useEffect(() => {
        if (!selected) return undefined;
        loadMessages(selected);
        return startInboxRecovery(() => loadMessages(selected), 15000);
    }, [selected, loadMessages]);

    const open = episode => {
        selectedId.current = episode.id;
        setMessages([]);
        setSelected(episode);
    };
    const close = () => { selectedId.current = null; setSelected(null); setMessages([]); };
    const download = async (url, filename) => {
        try { saveBlob(await (await read(url)).blob(), filename); }
        catch (e) { setError(e.message); }
    };
    const changeView = next => { close(); setOffset(0); setView(next); };
    const formatDate = value => value ? new Date(value).toLocaleString('es-ES') : '';

    return (
        <main className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
            <div className="mx-auto max-w-7xl space-y-6">
                <header className="flex flex-wrap items-center justify-between gap-4">
                    <div><h1 className="text-2xl font-bold">Supervisión de WhatsApp</h1>
                        <p className="text-sm text-slate-400">{profile?.agency_name || 'Mi agencia'} · Conversaciones de tus roles asignados</p></div>
                    <button onClick={logout} className="rounded-lg border border-slate-700 px-4 py-2">Cerrar sesión</button>
                </header>
                {error && <p role="alert" className="rounded-lg border border-red-800 bg-red-950/30 p-3 text-red-200">{error}</p>}
                <div className="flex flex-wrap items-center gap-3">
                    <button aria-pressed={view === 'operational'} onClick={() => changeView('operational')}
                        className={`rounded-lg border px-4 py-2 ${view === 'operational' ? 'border-emerald-500 text-emerald-300' : 'border-slate-700'}`}>En atención</button>
                    <button aria-pressed={view === 'history'} onClick={() => changeView('history')}
                        className={`rounded-lg border px-4 py-2 ${view === 'history' ? 'border-emerald-500 text-emerald-300' : 'border-slate-700'}`}>Historial</button>
                    <input aria-label="Buscar cliente o teléfono" placeholder="Buscar cliente o teléfono" value={search}
                        onChange={e => { close(); setSearch(e.target.value); setOffset(0); }} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2" />
                    <button onClick={refresh} className="rounded-lg border border-slate-700 px-4 py-2">Actualizar</button>
                    <button onClick={() => download(`/api/agency/export?${params}`, 'conversaciones-agencia.csv')}
                        className="rounded-lg border border-slate-700 px-4 py-2">Exportar CSV</button>
                </div>
                <p className="text-sm text-slate-400">Solo aparecen conversaciones que han necesitado atención de tu agencia. Actualización cada 15 segundos.</p>
                <div className="overflow-x-auto rounded-xl border border-slate-800">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-900 text-slate-400"><tr>{['Cliente', 'Rol', 'Campaña', 'Estado', 'Inicio', ''].map((label, i) => <th key={i} className="p-4">{label}</th>)}</tr></thead>
                        <tbody>{data.episodes.map(episode => <tr key={episode.id} className="border-t border-slate-800">
                            <td className="p-4">{episode.contact_name || 'Sin nombre'}<span className="block text-slate-400">{episode.phone_number}</span></td>
                            <td className="p-4">{episode.role_name}</td><td className="p-4">{episode.campaign_name || 'Entrada general'}</td>
                            <td className="p-4">{episode.status === 'open' ? 'En atención' : 'Finalizada'}</td>
                            <td className="p-4">{formatDate(episode.started_at)}</td>
                            <td className="p-4"><button onClick={() => open(episode)} className="text-emerald-300 underline">Ver conversación</button></td>
                        </tr>)}</tbody>
                    </table>
                    {!data.episodes.length && <p className="p-8 text-center text-slate-400">{loading ? 'Cargando…' : 'No hay conversaciones en esta vista.'}</p>}
                </div>
                <div className="flex items-center gap-4 text-sm">
                    <button disabled={!offset} onClick={() => setOffset(Math.max(0, offset - 50))} className="disabled:opacity-30">Anterior</button>
                    <span>{data.total} conversaciones</span>
                    <button disabled={offset + 50 >= data.total} onClick={() => setOffset(offset + 50)} className="disabled:opacity-30">Siguiente</button>
                </div>
            </div>
            {selected && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3">
                <section role="dialog" aria-modal="true" aria-label="Conversación" className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-slate-700 bg-slate-900">
                    <header className="flex justify-between gap-3 border-b border-slate-800 p-4"><div><h2>{selected.contact_name || selected.phone_number}</h2>
                        <p className="text-xs text-slate-400">{selected.campaign_name || 'Entrada general'} · Solo lectura</p></div><button onClick={close}>Cerrar</button></header>
                    <div className="space-y-4 overflow-y-auto p-4">
                        {!messages.length && <p className="text-sm text-slate-400">Sin mensajes que mostrar.</p>}
                        {messages.map(message => <div key={message.id} className={`flex ${String(message.direction).toLowerCase() === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                            <div className="max-w-[85%] rounded-xl border border-slate-700 bg-slate-800 p-3">
                                <p className="whitespace-pre-wrap break-words text-sm">{message.text_body}</p>
                                <WhatsAppInteractiveOptions message={message} />
                                {(message.attachments || []).map(att => <button key={att.id} className="mt-2 block text-sm text-emerald-300 underline"
                                    onClick={() => download(`/api/agency/episodes/${selected.id}/attachments/${att.id}`, att.filename || 'adjunto')}>{att.filename || 'Descargar adjunto'}</button>)}
                                <time className="mt-2 block text-[10px] text-slate-400">{formatDate(message.created_at)}</time>
                            </div>
                        </div>)}
                    </div>
                </section>
            </div>}
        </main>
    );
}
